// EmailConfirmDialog.tsx

import { auth } from "@/firebaseApp";
import { ROUTES } from "@/Routes";
import {
    Dialog,
    DialogContent,
    Typography,
    TextField,
    Button,
} from "@mui/material";
import { signInWithEmailLink, isSignInWithEmailLink } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { rehydrateBooking, useAppDispatch } from "@/redux";


export function EmailConfirmDialog() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showError, setShowError] = useState(false)
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const dispatch = useAppDispatch();

    const hasCompletedRef = useRef(false);

    const completeEmailSignIn = async (emailFunc: string) => {
        try {
            await signInWithEmailLink(auth, emailFunc, window.location.href);

            localStorage.removeItem("emailForSignIn");

            const pathname = window.location.pathname;

            if (pathname.includes("routeconfirmation")) {
                const routeId = pathname.split("/")[2];
                await handleRehydrateApp(routeId);
                return;
            }

            navigate(ROUTES.HOME, {  // TODO : CHANGE TO HOME
                replace: true,
            });

        } catch (e) {
            console.error(e);

            navigate(ROUTES.SIGN_IN, {
                replace: true,
            });
        }
    };

    useEffect(() => {
        // Prevent React StrictMode double execution
        if (hasCompletedRef.current) return;

        hasCompletedRef.current = true;

        const complete = async () => {
            // Not a Firebase email link
            if (!isSignInWithEmailLink(auth, window.location.href)
            ) { return }

            // Try localStorage first
            const storedEmail = localStorage.getItem("emailForSignIn");

            // No email -> open dialog
            if (!storedEmail) {
                setOpenEmailDialog(true);
                return;
            }

            await completeEmailSignIn(storedEmail);
        };

        complete();
    }, []);

    const handleEmailSubmit = async () => {
        if (!emailInput) return;
        setOpenEmailDialog(false);

        completeEmailSignIn(emailInput)

    };

    const handleRehydrateApp = async (id: string) => {

        dispatch(rehydrateBooking(id))
            .unwrap()
            .then(() => {
                navigate(
                    ROUTES.ROUTES_PREVIEW
                );
            });

    }

    const checkSubmit = () => {
        const isValidEmail = (email: string) =>
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!isValidEmail(emailInput)) {
            setShowError(true);
            return;
        }
        setLoading(true)
        handleEmailSubmit();
    }

    return (
        <Dialog
            open={openEmailDialog}
            onClose={() => setOpenEmailDialog(false)}
            fullWidth
            maxWidth="xs"
        >
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    p: 4,
                    textAlign: "center",
                }}
            >
                <Typography variant="h6">
                    Confirm your email
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    Enter the email used to request the sign-in link.
                </Typography>

                <TextField
                    fullWidth
                    placeholder="you@example.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") checkSubmit();
                    }}
                    error={showError}
                    helperText={showError ? "Enter a valid email address" : ""}
                    autoFocus
                />

                <Button
                    variant="contained"
                    onClick={checkSubmit}
                    disabled={!emailInput || loading}
                >
                    {loading ? "Signing in..." : "Continue"}
                </Button>
            </DialogContent>
        </Dialog>
    );
}